import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./TradeEntryConfluenceProgressTable.css";
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import TradingViewChartIcon from "./TradingViewChartIcon";
import { useWorkspace } from "./workspace/WorkspaceContext";

const BASE_SERVICE_URL = "/api/bot/ui/get";

function display(value) {
  return value === undefined || value === null || value === "" ? "-" : value;
}

const FILTER_DEFAULTS = {
  id: "",
  tradeEntryTrackerId: "",
  strategyId: "",
  ruleId: "",
  symbol: "",
  tradeSide: "ALL",
  timeFrame: "",
  conditionType: "ALL",
  signalKey: "",
  matched: "ALL",
  matchedDirection: "ALL",
  matchedCandleCloseTime: "",
  lastCheckedCandleCloseTime: "",
  status: "ALL",
  statusReason: "",
  signalRequestId: "",
  skipLogId: "",
  modifiedDate: "",
};

function uniqueOptions(rows, field) {
  return Array.from(new Set(rows.map((row) => row[field]).filter((value) => value !== undefined && value !== null && value !== ""))).sort();
}

function matchesText(value, filter) {
  return !filter || String(value ?? "").toLowerCase().includes(filter.trim().toLowerCase());
}

function TradeEntryConfluenceProgressTable() {
  const { workspace } = useWorkspace();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchRows = useCallback(() => {
    setLoading(true);
    setError("");

    fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/confluenceProgressAll`, workspace), {
      headers: getApiHeaders(),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => setRows(ensureArray(data)))
      .catch((err) => {
        console.error("Confluence progress fetch failed:", err);
        setError("Unable to load confluence progress right now.");
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [workspace]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const selectOptions = useMemo(() => ({
    tradeSide: uniqueOptions(rows, "tradeSide"),
    conditionType: uniqueOptions(rows, "conditionType"),
    matchedDirection: uniqueOptions(rows, "matchedDirection"),
    status: uniqueOptions(rows, "status"),
  }), [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchedValue = row.matched ? "YES" : "NO";
      return matchesText(row.id, filters.id)
        && matchesText(row.tradeEntryTrackerId, filters.tradeEntryTrackerId)
        && matchesText(row.strategyId, filters.strategyId)
        && matchesText(row.ruleId, filters.ruleId)
        && matchesText(row.symbol, filters.symbol)
        && (filters.tradeSide === "ALL" || row.tradeSide === filters.tradeSide)
        && matchesText(row.timeFrame, filters.timeFrame)
        && (filters.conditionType === "ALL" || row.conditionType === filters.conditionType)
        && matchesText(row.signalKey, filters.signalKey)
        && (filters.matched === "ALL" || matchedValue === filters.matched)
        && (filters.matchedDirection === "ALL" || row.matchedDirection === filters.matchedDirection)
        && matchesText(row.matchedCandleCloseTime, filters.matchedCandleCloseTime)
        && matchesText(row.lastCheckedCandleCloseTime, filters.lastCheckedCandleCloseTime)
        && (filters.status === "ALL" || row.status === filters.status)
        && matchesText(row.statusReason, filters.statusReason)
        && matchesText(row.signalRequestId, filters.signalRequestId)
        && matchesText(row.skipLogId, filters.skipLogId)
        && matchesText(row.modifiedDate, filters.modifiedDate);
    });
  }, [rows, filters]);

  const updateFilter = (field, value) => {
    setFilters((previous) => ({ ...previous, [field]: value }));
  };

  const textFilter = (field, placeholder) => (
    <input
      className="column-filter-input"
      value={filters[field]}
      placeholder={placeholder}
      onChange={(event) => updateFilter(field, event.target.value)}
    />
  );

  const selectFilter = (field, options, allLabel = "All") => (
    <select
      className="column-filter-select"
      value={filters[field]}
      onChange={(event) => updateFilter(field, event.target.value)}
    >
      <option value="ALL">{allLabel}</option>
      {options.map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
  );

  return (
    <div className="confluence-progress-container">
      <div className="confluence-progress-header">
        <div className="confluence-progress-title-block">
          <h2>Trade Entry Confluence Progress</h2>
          <p>Per-tracker rule state created by the confluence processor.</p>
        </div>
        <div className="confluence-progress-actions">
          <button className="btn-add" onClick={fetchRows} disabled={loading}>{loading ? "Loading..." : "Refresh"}</button>
          <button className="btn-neutral" onClick={() => setFilters(FILTER_DEFAULTS)}>
            <span className="btn-neutral__icon" aria-hidden="true" />
            Clear Filters
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-wrapper">
        <table className="attempt-log-table confluence-progress-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Tracker ID</th>
              <th>Strategy ID</th>
              <th>Rule ID</th>
              <th>Symbol</th>
              <th>Chart</th>
              <th>Side</th>
              <th>TF</th>
              <th>Condition</th>
              <th>Signal</th>
              <th>Matched</th>
              <th>Direction</th>
              <th>Matched Close</th>
              <th>Last Checked</th>
              <th>Status</th>
              <th>Reason</th>
              <th>Signal Request</th>
              <th>Skip Log</th>
              <th>Modified</th>
            </tr>
            <tr className="column-filter-row">
              <th>{textFilter("id", "ID")}</th>
              <th>{textFilter("tradeEntryTrackerId", "Tracker")}</th>
              <th>{textFilter("strategyId", "Strategy")}</th>
              <th>{textFilter("ruleId", "Rule")}</th>
              <th>{textFilter("symbol", "Symbol")}</th>
              <th />
              <th>{selectFilter("tradeSide", selectOptions.tradeSide, "All Sides")}</th>
              <th>{textFilter("timeFrame", "TF")}</th>
              <th>{selectFilter("conditionType", selectOptions.conditionType, "All Conditions")}</th>
              <th>{textFilter("signalKey", "Signal")}</th>
              <th>{selectFilter("matched", ["YES", "NO"], "All")}</th>
              <th>{selectFilter("matchedDirection", selectOptions.matchedDirection, "All")}</th>
              <th>{textFilter("matchedCandleCloseTime", "Matched")}</th>
              <th>{textFilter("lastCheckedCandleCloseTime", "Checked")}</th>
              <th>{selectFilter("status", selectOptions.status, "All Status")}</th>
              <th>{textFilter("statusReason", "Reason")}</th>
              <th>{textFilter("signalRequestId", "Signal Req")}</th>
              <th>{textFilter("skipLogId", "Skip")}</th>
              <th>{textFilter("modifiedDate", "Modified")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr><td colSpan="19">{loading ? "Loading confluence progress..." : "No confluence progress rows found."}</td></tr>
            )}
            {filteredRows.map((row) => (
              <tr key={row.id}>
                <td>{display(row.id)}</td>
                <td>{display(row.tradeEntryTrackerId)}</td>
                <td>{display(row.strategyId)}</td>
                <td>{display(row.ruleId)}</td>
                <td>{display(row.symbol)}</td>
                <td>
                  <TradingViewChartIcon
                    symbol={row.symbol}
                    timeFrame={row.timeFrame}
                    exchange={row.exchange || workspace.exchange}
                    marketType={row.marketType || workspace.marketType}
                  />
                </td>
                <td>{display(row.tradeSide)}</td>
                <td>{display(row.timeFrame)}</td>
                <td>{display(row.conditionType)}</td>
                <td>{display(row.signalKey)}</td>
                <td className={row.matched ? "matched-yes" : "matched-no"}>{row.matched ? "YES" : "NO"}</td>
                <td>{display(row.matchedDirection)}</td>
                <td>{display(row.matchedCandleCloseTime)}</td>
                <td>{display(row.lastCheckedCandleCloseTime)}</td>
                <td>{display(row.status)}</td>
                <td className="reason-cell">{display(row.statusReason)}</td>
                <td>{display(row.signalRequestId)}</td>
                <td>{display(row.skipLogId)}</td>
                <td>{display(row.modifiedDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="confluence-progress-record-count">
        Showing {filteredRows.length} of {rows.length} records
      </div>
    </div>
  );
}

export default TradeEntryConfluenceProgressTable;
